"use client";
import SkeletonJobSearch from "SkeletonJobSearch";
import AppliedFilters from "components/AppliedFilters/AppliedFilters";
import Fallback from "components/Fallback/Fallback";
import FilterGroups from "components/FilterGroups/FilterGroups";
import SkeletonFilters from "components/FilterGroups/SkeletonFilters";
import Jobs from "components/Jobs/Jobs";
import SkeletonJobs from "components/Jobs/SkeletonJobs";
import JobMap from "components/Maps/GoogleMap";
import ResultsHeader from "components/ResultsHeader/ResultsHeader";
import SearchBar from "components/SearchBar/SearchBar";
import { useAtom } from "jotai";
// import { DevTools } from "jotai-devtools";
import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import "react-tabs/style/react-tabs.scss";
import { useNavigate, Routes, Route } from "react-router-dom";
import { useLocation } from "react-router-dom";
import useErrorLogging from "./hooks/useErrorLogging";
import usePromoBanner from "./hooks/usePromoBanner";
import useSearchAndFilter from "./hooks/useSearchAndFilter";
import useJobDetails from "./hooks/useJobDetails";
import "./i18n";
import featureService from "./services/FeatureService";
import customURLSearchParams from "./services/CustomURLSearchParams";
import overrideService from "services/OverrideService";
import { useJobSearchType } from "./hooks/useJobSearchType";
import {
  errorMessageAtomString,
  isGridViewAtomBool,
  isLoadingAtomBool,
  isLoadingSkeletonAtomBool,
  originalDataAtomObject,
  searchPerformedAtomBool,
  showMapAtomBool,
  filtersAtomObject
} from "./state";
import JobDetailsPage from "components/Jobs/JobDetailsPage";

const MAP_FEATURE_FLAG = "sULI9BnTn8";

export default function App() {
  // Atom state
  const [originalData] = useAtom(originalDataAtomObject);
  const [searchPerformed] = useAtom(searchPerformedAtomBool);
  const [isLoading] = useAtom(isLoadingAtomBool);
  const [isLoadingSkeleton] = useAtom(isLoadingSkeletonAtomBool);
  const [errorMessage] = useAtom(errorMessageAtomString);
  const [isGridView, setIsGridView] = useAtom(isGridViewAtomBool);
  const [showMap, setShowMap] = useAtom(showMapAtomBool);
  const [filters, setFilters] = useAtom(filtersAtomObject);

  const { isJobPlugin } = useJobSearchType(originalData);


  // Hooks
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const searchAndFilter = useSearchAndFilter();
  const errorLogger = useErrorLogging();


  // Refs
  const lastJobItemRef = useRef(null);
  const firstLoad = useRef(false);
  const location = useLocation();
  // Promo banner hook
  usePromoBanner();

  // Get DOM element and filter values
  const vennture_dynamic_jobsearch = document.querySelector("#vennture_dynamic_jobsearch");
  const locationFilterValue = vennture_dynamic_jobsearch?.getAttribute("data-filter-location");
  const keywordFilterValue = vennture_dynamic_jobsearch?.getAttribute("data-filter-keyword");

  // Set organisation filter
  if (window.ConnectorDynamicSearchArgs) {
    window.ConnectorDynamicSearchArgs.organisation =
      vennture_dynamic_jobsearch?.getAttribute("data-filter-organisation");
  }

  // URL params and config
  const lang = customURLSearchParams().get("override-lang");
  const loginUrl = window.ConnectorDynamicSearchArgs?.loginUrl;

  // Computed values
  const hasMapFeature = featureService.isAllowed([MAP_FEATURE_FLAG]);
  const searchParams = new URLSearchParams(window.location.search);
  // const jobId = searchParams.get("job");
  const jobSlug = searchParams.get("job");


  const getJobIdBySlug = (slug) => {
    if (!slug || !originalData.jobs) return null;

    // First try exact match
    let job = originalData.jobs.find(j => j.slug === slug);

    // If no exact match, try contains match
    if (!job) {
      job = originalData.jobs.find(j => j.slug && j.slug.includes(slug));
    }

    return job ? job.id : null;
  };




  const jobId = getJobIdBySlug(jobSlug);
  console.log(`Found job ID for slug "${jobSlug}":`, jobId);


  // Initialize language
  useEffect(() => {
    const defaultLang = window.ConnectorDynamicSearchArgs?.language ?? "en";
    i18n.changeLanguage(lang || defaultLang);
  }, [lang, i18n]);



  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!lastJobItemRef.current || !originalData.nextPageToken) return;

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && originalData.nextPageToken) {
        searchAndFilter.search(originalData.nextPageToken);
      }
    }, options);

    observer.observe(lastJobItemRef.current);

    return () => {
      if (lastJobItemRef.current) {
        observer.unobserve(lastJobItemRef.current);
      }
    };
  }, [lastJobItemRef.current, originalData.nextPageToken, searchAndFilter]);

  // Event handlers
  const toggleMap = useCallback(() => {
    if (!hasMapFeature) return false;
    setShowMap(prev => {
      const newShowMap = !prev;
      document.body.classList.toggle("map-open", newShowMap);
      return newShowMap;
    });
  }, [hasMapFeature, setShowMap]);

  const toggleGridView = useCallback(() => {
    setIsGridView(prev => !prev);
  }, [setIsGridView]);

  // Render helpers
  const renderErrorBoundary = (component, componentName) => (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={errorLogger.logError}
      beforeCapture={(scope) => {
        scope.setTag("location", "app.js");
        scope.setTag("component", componentName);
      }}
    >
      {component}
    </ErrorBoundary>
  );



  const renderNoResults = () => (
    <div className="no-results">
      <div className="formholder">
        <div className="message">
          <h3>{t("error_title")}</h3>
          <div className="rte">
            <p>
              {t("error_body")} {!loginUrl && "."}{" "}
              {loginUrl && t("error_body2")}{" "}
              {loginUrl && (
                <a href={loginUrl}>
                  {t("error_logintext")}
                </a>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderJobResults = () => (
    <Row>
      <Col>
        {isLoadingSkeleton && !searchPerformed ? (
          <SkeletonFilters />
        ) : (
          searchPerformed && <FilterGroups />
        )}
      </Col>
      <Col>
        {!isLoading && originalData.totalCount > 0 && (
          <ResultsHeader
            toggleMap={toggleMap}
            toggleGridView={toggleGridView}
            showMap={hasMapFeature}
          />
        )}

        {errorMessage && (
          <div className="error">
            <p>{t("error_title")}</p>
          </div>
        )}

        <div className={`job-results ${showMap ? "remove-scroll-overflow" : ""}`}>
          <ul className={`job-listing ${isGridView ? "card-view" : ""}`}>
            {!isLoading && originalData && (
              <>
                {renderErrorBoundary(<Jobs lastJobItemRef={lastJobItemRef} />, "Jobs")}
                {isLoadingSkeleton && <SkeletonJobs />}
              </>
            )}
            {isLoading && <SkeletonJobs />}
          </ul>

          {!isLoading && searchPerformed && originalData.totalCount === 0 && renderNoResults()}
        </div>

        {showMap && renderErrorBoundary(
          <JobMap toggleMap={toggleMap} nextPageSearch={searchAndFilter.search} />,
          "JobMap"
        )}
      </Col>
    </Row>
  );
  const currentJob = useMemo(() => {
    if (!jobSlug || !originalData.jobs) return null;

    let job = originalData.jobs.find(j => j.slug === jobSlug);
    let isExactMatch = !!job;

    if (!job && originalData.jobs.length > 0) {
      job = originalData.jobs.find(j => j.slug && j.slug.includes(jobSlug));

      if (!job) {
        job = originalData.jobs[0];
      }

      if (job && job.slug !== jobSlug) {
        // console.log(`Updating URL from "${jobSlug}" to "${job.slug}"`);
        window.history.replaceState({}, document.title, `${window.location.pathname}?job=${job.slug}`);
      }
    }

    return job;
  }, [jobSlug, originalData.jobs]);

  // console.log("Current job:", currentJob);

  const searchResults = () => (
    <>
      <SearchBar
        locationFilterValue={locationFilterValue}
        keywordFilterValue={keywordFilterValue}
      />
      <div className="padding-bottom vsm-job-search-results-listings">
        <div className="bg">
          <div className="component-wrapper">
            <div className="vsm-body">
              <Container>
                <Row>
                  <Col>
                    {searchPerformed && renderErrorBoundary(
                      <AppliedFilters clearFilter={searchAndFilter.handleClearFilter} />,
                      "AppliedFilters"
                    )}
                  </Col>
                </Row>
                {isLoadingSkeleton && !searchPerformed && (
                  <SkeletonJobSearch />
                )}
                {(!isLoadingSkeleton || searchPerformed) && renderJobResults()}
              </Container>
            </div>
          </div>
        </div>
      </div>
    </>
  )


  const renderJobDetails = () => {
    if (currentJob?.url && currentJob.url !== "" && currentJob.url !== "#") {
      window.location.href = currentJob.url;
      return null;
    }
    // console.log("Rendering JobDetailsPage for slug:",currentJob, jobSlug);
    return <JobDetailsPage slug={jobSlug} id={jobId} />;
  };


  return (
    <>
      {!jobSlug ? (
        <>{searchResults()}</>
      ) : (
        renderErrorBoundary(renderJobDetails(), "JobDetails")
      )}
    </>
  );
}